import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input, InputSignal, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { RegisterUserFormService } from 'src/app/pages/register-user/services/register-user-form.service';
import { BaseFormField } from 'src/app/pages/register-user/shared/form/abstracts';
import { DateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { FormStepperService } from '../../../components/register-user-form-mobile/services';
import { DateFieldService } from './services/date-field.service';

@Component({
  selector: 'app-date-field',
  standalone: true,
  imports: [
    TranslateModule, 
    MatFormFieldModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatDatepickerModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi:true,
      useExisting: DateFieldComponent
    }
  ],
  templateUrl: './date-field.component.html',
  styleUrls: ['./date-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFieldComponent extends BaseFormField<Date> implements OnInit, OnDestroy {
  // ViewChilds
  public calendarRef = viewChild<MatCalendar<Date>>('calendarRef');
  
  // Lifecycle
  ngOnInit(){
    this.restoreDateFromForm();
  }

  ngOnDestroy(): void {
    this._dateFieldService.resetState();
  }  
  
  // Inputs
  public form: InputSignal<FormGroup> = input.required<FormGroup>();
  public formControlNameStartDate: InputSignal<string> = input.required<string>();
  public formControlNameEndDate: InputSignal<string> = input.required<string>();
  
  // Services
  private _dateFieldService = inject(DateFieldService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _formStepperService = inject(FormStepperService);
  private _selectionModel = inject(MatRangeDateSelectionModel<Date>)
  private _selectionStrategy = inject(DefaultMatCalendarRangeStrategy<Date>);
  private _adapter: DateAdapter<Date> = inject(DateAdapter);
  private _cdr = inject(ChangeDetectorRef); 

  // Computed properties
  private _startDateControl = computed(() => {
    const control = this.form().get(this.formControlNameStartDate());
    return control as FormControl;
  });
  private _endDateControl = computed(() => {
    const control = this.form().get(this.formControlNameEndDate());
    return control as FormControl;
  });
  
  // Service signals
  private _isNextButtonClicked = this._formStepperService.isNextButtonClicked;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;
  protected localeCode = this._dateFieldService.localeCode;
  
  // Datepicker properties
  public minDate = this._dateFieldService.minDate;
  public selectedDateRange: DateRange<Date> | undefined;
  
  // Effects
  markAllFieldsAsTouchedForDesktop = effect(() => {
    const startControl = this._startDateControl();
    const endControl = this._endDateControl();
    
    if (startControl && endControl) {
      this._dateFieldService.markControlsAsTouchedForDesktop(
        startControl,
        endControl,
        this._markAllFormFieldsAsTouched()
      );
      
      // Trigger change detection manually for OnPush
      this._cdr.detectChanges();
    }
  }, { allowSignalWrites: true });
  
  loadCalendarLanguage = effect(() => {
    // Set first day of week based on locale
    this._adapter.getFirstDayOfWeek = () => this._dateFieldService.getFirstDayOfWeek();
    
    // Update calendar language
    this._adapter.setLocale(this.localeCode());
  }, { allowSignalWrites: true });
  
  // Methods
  public rangeChanged(selectedDate: Date): void {
    const selection = this._selectionModel.selection;
    const newSelection = this._selectionStrategy.selectionFinished(selectedDate, selection);

    this._selectionModel.updateSelection(newSelection, this);
    this.selectedDateRange = new DateRange<Date>(newSelection.start, newSelection.end);

    const startControl = this._getStartDateControl();
    const endControl = this._getEndDateControl();

    if (startControl && endControl) {
      this._dateFieldService.processDateRangeSelection(
        this.selectedDateRange,
        startControl,
        endControl
      );
    }
  }
  
  public getDate(type: 'start' | 'end'): string {
    return this._dateFieldService.getFormattedDate(type);
  }
  
  public isInvalidField(): boolean {
    const startControl = this._getStartDateControl();
    const endControl = this._getEndDateControl();
    
    if (!startControl || !endControl) return false;
    
    return this._dateFieldService.validateDateFields(
      startControl,
      endControl,
      this._isNextButtonClicked()
    );
  }

  // Private helper methods
  private _getStartDateControl(): FormControl | null {
    return this.form().get(this.formControlNameStartDate()) as FormControl;
  }

  private _getEndDateControl(): FormControl | null {
    return this.form().get(this.formControlNameEndDate()) as FormControl;
  }

  private restoreDateFromForm(){
    if (!this._startDateControl()?.value || !this._endDateControl()?.value)
      return;

    const startDate = this.stringToDate(this._startDateControl()?.value)
    const endDate = this.stringToDate(this._endDateControl()?.value)
    this.selectedDateRange = new DateRange<Date>(startDate, endDate);
    this._dateFieldService.processDateRangeSelection(
      this.selectedDateRange,
      this._startDateControl(),
      this._endDateControl()
    );
  }

  private stringToDate(dateStr) {
    const [day, month, year] = dateStr.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

}
